import { useState } from 'react';
import { BUILTIN_ICONS, parseIconRef, resolveIcon, type MapIcon, type ResolvedIcon } from '@realmery/realm-core';
import { IconArt } from './pins.tsx';

/**
 * Choosing an icon (docs/PARITY-STEP-4-PLAN.md section 5), as the desktop's `IconPicker`: the
 * built-in icons by group, then each pack imported into the realm, then emoji or a letter,
 * with a search box. The result is the icon reference to keep: "lf:castle", "img:<id>", the
 * text itself, or blank for the category's icon (a pin) or none (a category).
 */

/** `AtlasDialogs.ICON_CHOICES`: a fantasy-cartography palette of emoji. */
export const EMOJI_CHOICES = ['🏰', '🏯', '🏛', '🏘', '🏠', '🛖', '⛪', '🗼', '⚓', '⛵', '⚔', '🛡', '🗡', '💀', '☠', '🐉', '⛰', '🌋', '🏔', '🌲', '🌊', '🏝', '🕳', '🗿', '🍺', '💎', '🗝', '📜', '✦', '★', '⚑', '❓'];

type PictureUrl = (icon: ResolvedIcon & { kind: 'PICTURE' }) => string;

/** An icon on a disc of the colour it'll be shown on, as the picker and the forms show it. */
export function IconSwatch({ value, color, icons, pictureUrl, size = 28 }: { value: string; color: string; icons: ReadonlyMap<string, MapIcon>; pictureUrl: PictureUrl; size?: number }) {
  const icon = resolveIcon(parseIconRef(value), (id) => icons.get(id));
  return (
    <svg width={size} height={size} viewBox="-12 -12 24 24" aria-hidden="true" className="icon-swatch">
      <circle r={12} fill={color} />
      <IconArt icon={icon} cx={0} cy={0} size={icon?.kind === 'PICTURE' ? 24 : 15} clipId={`swatch-${value.replace(/[^A-Za-z0-9_-]/g, '')}`} pictureUrl={pictureUrl} />
    </svg>
  );
}

export function IconPicker({
  value,
  forPin,
  color,
  icons,
  pictureUrl,
  onPick,
  onCancel,
}: {
  value: string;
  /** A pin's own icon, which can fall back to its category's. */
  forPin: boolean;
  color: string;
  icons: readonly MapIcon[];
  pictureUrl: PictureUrl;
  onPick: (value: string) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const [typed, setTyped] = useState(parseIconRef(value).kind === 'EMOJI' ? value : '');
  const byId = new Map(icons.map((i) => [i.id, i]));
  const q = query.trim().toLowerCase();
  const matches = (name: string) => q === '' || name.toLowerCase().includes(q);
  const groups = new Map<string, { value: string; name: string }[]>();
  for (const icon of BUILTIN_ICONS) {
    if (!matches(icon.name) && !matches(icon.group)) continue;
    groups.set(icon.group, [...(groups.get(icon.group) ?? []), { value: icon.ref, name: icon.name }]);
  }
  for (const icon of icons) {
    if (!matches(icon.name) && !matches(icon.pack)) continue;
    groups.set(`Pack: ${icon.pack}`, [...(groups.get(`Pack: ${icon.pack}`) ?? []), { value: `img:${icon.id}`, name: icon.name }]);
  }
  const choice = (option: { value: string; name: string }) => (
    <button key={option.value} type="button" role="radio" aria-checked={option.value === value} aria-label={option.name} title={option.name} className="icon-choice" onClick={() => onPick(option.value)}>
      <IconSwatch value={option.value} color={color} icons={byId} pictureUrl={pictureUrl} />
    </button>
  );
  return (
    <div className="panel icon-picker" role="group" aria-label={forPin ? "Pin's icon" : "Category's icon"}>
      <p className="meta">{forPin ? "Pick this pin's icon, or keep its category's." : 'Pick the icon every pin in this category shows.'}</p>
      <div className="actions">
        <button type="button" aria-pressed={value.trim() === ''} onClick={() => onPick('')}>
          {forPin ? "Category's icon" : 'No icon'}
        </button>
        <label className="icon-search">
          <span className="visually-hidden">Search icons</span>
          <input type="search" value={query} placeholder="Search icons" onChange={(e) => setQuery(e.currentTarget.value)} />
        </label>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="icon-groups">
        {[...groups].map(([group, options]) => (
          <section key={group} aria-label={group}>
            <h4>{group}</h4>
            <div className="icon-choices" role="radiogroup" aria-label={group}>
              {options.map(choice)}
            </div>
          </section>
        ))}
        {(q === '' || 'emoji'.includes(q)) && (
          <section aria-label="Emoji">
            <h4>Emoji</h4>
            <div className="icon-choices" role="radiogroup" aria-label="Emoji">
              {EMOJI_CHOICES.map((e) => choice({ value: e, name: e }))}
            </div>
            <div className="icon-typed">
              <label>
                Or type an emoji or a letter
                <input
                  value={typed}
                  maxLength={8}
                  onChange={(e) => setTyped(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (typed.trim() !== '') onPick(typed.trim());
                  }}
                />
              </label>
              <button type="button" disabled={typed.trim() === ''} onClick={() => onPick(typed.trim())}>
                Use it
              </button>
            </div>
          </section>
        )}
        {groups.size === 0 && q !== '' && !'emoji'.includes(q) && <p className="meta">No icons match “{query.trim()}”.</p>}
      </div>
    </div>
  );
}
